/** Inline advice pack PDF preview for the agent review Artefacts step. */

import { useEffect, useState } from "react";

import { resolveAdviceDeckPreview } from "./adviceDeckPreview";
import type { ArtefactReviewItem } from "./types";

interface AdviceDeckArtefactPreviewProps {
  artefact: ArtefactReviewItem;
  className?: string;
}

export function AdviceDeckArtefactPreview({ artefact, className }: AdviceDeckArtefactPreviewProps) {
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;

    setIframeUrl(null);
    setNotice(null);
    setError(null);

    void resolveAdviceDeckPreview(artefact.artifact_id, artefact)
      .then((preview) => {
        if (cancelled) return;
        if (preview.type === "iframe") {
          setIframeUrl(preview.url);
          setNotice(preview.notice);
          return;
        }
        objectUrl = URL.createObjectURL(preview.blob);
        setIframeUrl(objectUrl);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Couldn't load advice pack preview.");
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [artefact.artifact_id, artefact.report_config_id, artefact.viewer_url, artefact.preview_path]);

  if (error) {
    return (
      <div className="rounded-xl bg-white/[0.02] px-4 py-6 text-sm text-slate-400 ring-1 ring-white/5">
        {error}
      </div>
    );
  }

  if (!iframeUrl) {
    return (
      <div className="rounded-xl bg-white/[0.02] px-4 py-6 text-sm text-slate-400 ring-1 ring-white/5">
        Loading preview…
      </div>
    );
  }

  return (
    <div
      className={`flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl bg-white text-slate-900 ring-1 ring-white/10 ${className ?? ""}`}
    >
      {notice ? (
        <p className="flex-shrink-0 border-b border-slate-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          {notice}
        </p>
      ) : null}
      <iframe
        title={`Advice pack — ${artefact.title}`}
        src={iframeUrl}
        className="min-h-0 flex-1 w-full border-0 bg-white"
      />
    </div>
  );
}
